import { computed } from 'vue';
import moment from 'moment-timezone';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import type { HelpTip, HelpVisibilityContext, WhatsNewEntry } from '../interfaces';
import type { ROLES } from '@AuthModule';
import { getShelterNow } from '@Constants';
import { useSessionStore } from '@Stores';
import { isInShelterLoginAsContext } from '@AuthModule';
import { HELP_TIPS } from '../constants';
import { WHATS_NEW_STATUS } from '../enums';
import {
  buildWhatsNewEntries,
  getEffectiveRequires,
  getEffectiveShowUntil,
  getHelpTipStatus,
  hasUnseenNews,
  isEntryNew,
  isHelpTipActive,
  isHiddenFromEverybody,
  isNavItemNew,
  meetsRequirements,
  resolveArticleLanguage,
} from '../helpers';
import useWhatsNewStore from '../store';
import useHelpState from './useHelpState';

export default function useHelpVisibility() {
  const { isSU, shelterProfile, userProfile } = storeToRefs(useSessionStore());
  const { can } = useSessionStore();
  const { articleEntries, tipTexts } = storeToRefs(useWhatsNewStore());
  const { fetchEntries } = useWhatsNewStore();

  const { locale } = useI18n();
  const { helpState } = useHelpState();

  const articleLanguage = computed(() => resolveArticleLanguage(locale.value));
  const entries = computed(() => buildWhatsNewEntries(articleEntries.value, helpTips.value, context.value));
  const hasUnseenEntries = computed(() => isInShelterContext.value && hasUnseenNews(entries.value, context.value));
  const isInShelterContext = computed(() => !isSU.value || isInShelterLoginAsContext(userProfile.value));

  const context = computed<HelpVisibilityContext>(() => ({
    can: (role: string) => can(role as ROLES),
    helpState: helpState.value,
    isSuperAdmin: !!isSU.value,
    shelterFlags: shelterProfile.value || {},
    // eslint-disable-next-line no-restricted-syntax -- ISO on purpose: compared as a string with a tip's `showUntil`, never shown
    today: moment(getShelterNow()).format('YYYY-MM-DD'),
  }));

  const helpTips = computed(() =>
    HELP_TIPS.map((tip) => ({
      ...tip,
      requires: getEffectiveRequires(tip.requires, tip.key, tipTexts.value) ?? undefined,
      showUntil: getEffectiveShowUntil(tip, tipTexts.value),
    }))
  );
  const tips = computed(() =>
    helpTips.value
      .filter((tip) => context.value.isSuperAdmin || meetsRequirements(tip.requires, context.value))
      .map((tip) => ({ ...tip, status: getHelpTipStatus(tip, context.value) }))
      .sort((first, second) => second.addedOn.localeCompare(first.addedOn))
  );

  // HELPERS
  function findTip(tipKey: string) {
    return helpTips.value.find(({ key }) => key === tipKey);
  }

  function getTipStatus(tipKey: string | null | undefined) {
    return tips.value.find(({ key }) => key === tipKey)?.status;
  }

  function checkTipActive(tip: HelpTip | undefined) {
    return !!tip && isHelpTipActive(tip, context.value);
  }

  function checkEntryHidden(entry: WhatsNewEntry) {
    return isHiddenFromEverybody(entry.requires);
  }

  function checkTipHidden(tip: HelpTip | undefined) {
    return isHiddenFromEverybody(tip?.requires);
  }

  function checkEntryNew(entry: WhatsNewEntry) {
    return isEntryNew(entry, context.value);
  }

  function checkFeatureVisible(tipKey: string) {
    return context.value.isSuperAdmin || meetsRequirements(findTip(tipKey)?.requires, context.value);
  }

  function checkNavItemNew(navKey: string) {
    return isNavItemNew(navKey, entries.value, context.value);
  }

  function findReadableArticle(articleKey: string | undefined) {
    if (!articleKey) return undefined;

    return entries.value.find(({ has_article, key, status }) => {
      const canRead = context.value.isSuperAdmin || status !== WHATS_NEW_STATUS.DRAFT;

      return key === articleKey && has_article && canRead;
    });
  }

  // EVENTS
  function loadEntries(shouldForce = false) {
    return fetchEntries(articleLanguage.value, shouldForce);
  }

  return {
    articleLanguage,
    checkEntryHidden,
    checkEntryNew,
    checkFeatureVisible,
    checkNavItemNew,
    checkTipActive,
    checkTipHidden,
    entries,
    findReadableArticle,
    findTip,
    getTipStatus,
    hasUnseenEntries,
    loadEntries,
  };
}
